import type {PanelPaymentDetails} from "../../blink-sdk/PanelOptionsLogic";
// TODO @flow rename stuff here explicitly to purchase
import {iFrameConnection} from "./IFrameConnection.js";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages.js";
import {TopUpPayment} from "../../client/state/TopUpPaymentStore.js";
import {Dispatchable, Dispatcher} from "../../stem-core/base/Dispatcher";
import {Currency} from "../../stem-core/localization/CurrencyStore.js";
import {iFrameMerchantService} from "./IframeMerchantService.js";
import {Product} from "../../client/state/ProductStore.js";
import {apiUserPurchaseProduct, type PurchaseProductRequest} from "../../client/state/PurchaseStore.js";
import {Money} from "../../stem-core/localization/Money.js";
import type {StoreId} from "../../stem-core/state/State";
import type {PaymentRequest} from "../../blink-sdk/modules/PurchaseModule";


// TODO @flow should this be in Flow.purchaseSomething?
class IFramePurchaseHandler extends Dispatchable {
    // Left untyped: attachListenerOnce is declared to answer with a CleanupJob, and only one member of
    // that union has the remove() called on it below - see the TODO on getAttachCleanupJobMethod
    autopayAfterReloadHandler = null;
    manualPaymentOfferId: StoreId | null = null;
    lastRequest: PaymentRequest | null = null;
    lastResponse: {request: PaymentRequest; response?: any; error?: any} | null = null;

    init() {
        // TODO @flow what reason is there for this?
        iFrameConnection.addIframesListener(IFrameMessages.MAKE_NEXT_PAYMENT_MANUALLY, ({offerId}: {offerId: StoreId}) => {
            this.manualPaymentOfferId = offerId;
        });

        iFrameConnection.addListener(IFrameMessages.PAYMENT_REQUEST, (request: PaymentRequest) => {
            if (request.offerId !== this.manualPaymentOfferId) {
                this.requestPayment(request, false).then();
                return;
            }
            this.manualPaymentOfferId = null;
            this.requestPayment(request, true).then();
        });
    }

    // A panel's own paymentDetails or a merchant's payment request, which is the same shape with the offer named
    getProduct(paymentRequest: PanelPaymentDetails) {
        if (paymentRequest.productId) {
            return Product.get(paymentRequest.productId);
        }
        const allowedProducts = Product.filterBy({allowArbitrarySubproducts: true});
        if (allowedProducts.length !== 1) {
            throw `Can't automatically fill in product, number of products with arbitrary subproduct: ${allowedProducts.length}`;
        }
        return allowedProducts[0];
    }

    // Helper method to allow the underlying request to be more flexible
    normalizeRequest(paymentRequest: PanelPaymentDetails) {
        const {
            amount,
            currencyIsoCode,
            currencyId,
            currency,
            resourceId,
            comment,
        } = paymentRequest;

        const merchant = iFrameMerchantService.getMerchant();
        const requestCurrency = Currency.get(currency || currencyId || currencyIsoCode) || merchant.getCurrency();
        const product = this.getProduct(paymentRequest);

        return {
            comment,
            resourceId,
            product: product,
            amount: new Money(amount, requestCurrency) || product.getAmount(), // Handles from String or Number in millionth of subunits
        }
    }

    async makePaymentRequest(paymentRequest: PaymentRequest, isAutomatic: boolean) {
        const {
            amount,
            product,
            resourceId,
            comment,
        } = this.normalizeRequest(paymentRequest);

        const merchant = iFrameMerchantService.getMerchant();

        const request: PurchaseProductRequest = {
            merchantId: merchant.id,
            ...amount.toJSON(),
            useWalletFunds: "yes",
            isAutomatic,
            products: {
                entries: [{
                    productId: product.id,
                    subproductId: resourceId,
                }],
            },
            ...comment, // TODO @flow why is this spread?
        }

        return await apiUserPurchaseProduct(request);
    }

    async requestPayment(request: PaymentRequest, isAutomatic = false) {
        this.lastRequest = request;

        try {
            const response = await this.makePaymentRequest(request, isAutomatic);
            this.lastResponse = {request, response};
        } catch (error) {
            this.lastResponse = {request, error};
        }

        iFrameConnection.sendToSDK(IFrameMessages.PAYMENT_RESPONSE, this.lastResponse);
    }

    remakeLastPaymentAfterFundsAdded() {
        if (this.autopayAfterReloadHandler) {
            this.autopayAfterReloadHandler.remove();
        }
        // TODO @flow send the request payload to the widget and have them resend it
        this.autopayAfterReloadHandler = this.attachListenerOnce(TopUpPayment, "create", () => {
            this.attachListenerOnce(Dispatcher.Global, "balanceAnimationFinished", () => {
                this.autopayAfterReloadHandler = null;
                this.requestPayment(this.lastRequest, false);
            });
        });
    }
}

export const iFramePurchaseHandler = new IFramePurchaseHandler();
